import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { type Dispatch, type UnknownAction } from "redux";
import { Users } from "lucide-react";

import { listFarmersTableCounts, listFarmersTableCountsReset } from "../../actions";
import { PageBreadcrumb } from "../../components/PageBreadcrumb";
import { displayErrorMessage, getCountryObjectFromCache } from "../../utils";
import { FarmersTable } from "./FarmersTable";
import { useFarmersTable } from "./useFarmersTable";
import { type FarmerQuickFilterId } from "./quick-filters";

type QuickFilterCounts = Partial<Record<FarmerQuickFilterId, number>>;

interface RootState {
  listFarmersTableCounts: {
    data: QuickFilterCounts;
  };
}

export const Farmers = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<Dispatch<UnknownAction>>();
  const countryName = getCountryObjectFromCache()?.name ?? "";

  const { errorMessage, ...farmersTable } = useFarmersTable();
  const quickFilterCounts = useSelector((state: RootState) => state.listFarmersTableCounts.data);

  // The counts endpoint ignores the search and the tile filters, so one request per mount.
  useEffect(() => {
    dispatch(listFarmersTableCounts() as unknown as UnknownAction);

    return () => {
      dispatch(listFarmersTableCountsReset() as unknown as UnknownAction);
    };
  }, [dispatch]);

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-col gap-4">
        <PageBreadcrumb
          items={[
            { label: t("header.overview"), href: "/wfpManager" },
            { label: t("home.manager.overview.farmersModal.breadcrumb") },
          ]}
        />
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-x-2">
            <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 text-aqua-600 bg-aqua-100">
              <Users />
            </span>
            <h1 className="font-semibold text-2xl">
              {t("home.manager.overview.farmersModal.title", { country: countryName })}
            </h1>
          </div>
          <p className="text-muted-foreground">
            {t("home.manager.overview.farmersModal.description")}
          </p>
        </div>
      </div>

      {displayErrorMessage(errorMessage)}

      <FarmersTable {...farmersTable} quickFilterCounts={quickFilterCounts} showQuickFilters />
    </main>
  );
};
